import { Component, HostListener, ViewChild } from '@angular/core';
import { IonRouterOutlet, ModalController, Platform } from '@ionic/angular';
import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { NavigationEnd, Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import {
  IonicUtilityService,
  FcmService,
  NetworkStatusService,
} from '@viso/core/utilities';
import { Location } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PushNotification } from '@capacitor/push-notifications';
import { AppSettingsService, AuthService, FcmDataService, FireAnalyticsService, MaintenanceService, NotificationService, UserService } from '@viso/core/data-access';
import { User } from '@viso/core/models';
import { filter } from 'rxjs';

@Component({
  selector: 'viso-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent {
  appExitModalInstance = null;
  updateAlertInstance = null;
  @ViewChild(IonRouterOutlet, { static: true }) routerOutlet: IonRouterOutlet;
  user: User;
  isNative = Capacitor.getPlatform() !== 'web';
  showMaintenance = false;
  message = '';

  private readonly publicKey =
    'BORwy1LDP4uqAeC-WMI9aUtMgIPncZXKhlYLQEYlKwqklxI3DBiwTzvgv6R5xVNwqWAoTzThR3HYFvrw80LAlWc';
  constructor(
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private router: Router,
    private modal: ModalController,
    private location: Location,
    private ionicUtility: IonicUtilityService,
    private auth: AuthService,
    private appSettings: AppSettingsService,
    private userService: UserService,
    private fcmService: FcmService,
    private swPush: SwPush,
    private fcmDataService: FcmDataService,
    private networkStatus: NetworkStatusService,
    private fireAnalytics: FireAnalyticsService,
    private maintenanceService: MaintenanceService,
    private notification: NotificationService
  ) {
    this.initializeApp();
  }

  initializeApp() {
    // this.requestNotificationPermission();
    // this.onNotificationReceive();
    // this.onNotificationsAction();
    this.ionicUtility.triggerOfflineToast();

    this.networkStatus.initializeNetworkMonitor();

    this.fireAnalytics.setEnabled(true);

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.fireAnalytics.setCurrentScreen(event.urlAfterRedirects);
    });

    this.platform.ready().then(() => {
      if (this.isNative) {
        this.getPushNotifications();
        this.getActionPerformedPushNotifications();
        this.fcmService.startRegistration();
      }
      this.getFcmToken();
      this.statusBar.styleDefault();
      this.splashScreen.hide();
      this.backButtonEvent();
    });
    this.getSetting();
    this.notification.startRegistration();
    this.platform.resume.subscribe(() => {
      this.getSetting();
    });
    this.maintenanceService.maintenanceEnabled.subscribe(status => {
      this.showMaintenance = status;
      // console.log('Maintenance status:', status);
    });
    this.maintenanceService.maintenanceMessage.subscribe(msg => {
      this.message = msg;
    });
  }

  async getSetting() {
      const platform = Capacitor.getPlatform().toLowerCase();
      if (['ios', 'android'].includes(platform)) {
        const params = [
          `is-${platform}-update`,
          `${platform}-version`,
          'partner-playstore-link',
          'partner-appstore-link'
        ];
  
        this.appSettings.getSettings(params).subscribe(async (resp) => {
  
          const isUpdate = resp.find(r => r.app_key === `is-${platform}-update`);
          if (isUpdate?.app_value === 'yes') {
            const d = await App.getInfo();
            const versionInfo = resp.find(r => r.app_key === `${platform}-version`);
            const linkKey = platform === 'android' ? 'partner-playstore-link' : 'partner-appstore-link';
            const appLink = resp.find(r => r.app_key === linkKey);
  
            if (versionInfo && d.version !== versionInfo.app_value) {
              console.log("Version Mismatch Detected");
              if (appLink) {
                this.showUpdateAppDialog(appLink.app_value);
              }
            }
          }
        });
      }
    }

  async showUpdateAppDialog(link) {
    if (this.updateAlertInstance === null) {
      this.updateAlertInstance = await this.ionicUtility.showAlert(
        'New version of app available please download latest version',
        null,
        '',
        '',
        [
          {
            text: 'Update',
            cssClass: 'secondary',
            handler: () => {
              this.updateAlertInstance = null;
              window.open(link, '_system');
            },
          },
        ],
        false
      );
      await this.updateAlertInstance.present();
    }
  }

  async presentAlertConfirm() {
    const alert = await this.ionicUtility.showAlert(
      'Are you sure you want to exit the app?',
      null,
      '',
      '',
      [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            this.appExitModalInstance = null;
          },
        },
        {
          text: 'Close App',
          handler: () => {
            this.appExitModalInstance = null;
            App.exitApp();
          },
        },
      ]
    );
    await alert.present();
    this.appExitModalInstance = alert;
  }

  backButtonEvent() {
    document.addEventListener('backbutton', async () => {
      if (this.appExitModalInstance) return;
      if (this.auth.isLoggedIn()) {
        if (this.router.url !== '/event/list') {
          await this.location.back();
        }
        if (this.router.url === '/event/list') {
          await this.presentAlertConfirm();
        }
      } else {
        if (this.router.url !== '/welcome') {
          await this.location.back();
        }
        if (this.router.url === '/welcome') {
          await this.presentAlertConfirm();
        }
      }
    });
    // this.platform.backButton.subscribeWithPriority(-1, async () => {
    //   if (this.auth.isLoggedIn()) {
    //     if (this.router.url !== '/event/list') {
    //       await this.location.back();
    //     }
    //     if (this.router.url === '/event/list') {
    //       await this.presentAlertConfirm();
    //     }
    //   } else {
    //     if (this.router.url !== '/welcome') {
    //       await this.location.back();
    //     }
    //     if (this.router.url === '/welcome') {
    //       await this.presentAlertConfirm();
    //     }
    //   }
    // });
  }

  getPushNotifications() {
    this.fcmService.getReceivedPushNotification().subscribe((notification) => {
      // TODO: Logic for on push notification received
    });
  }

  getActionPerformedPushNotifications() {
    this.fcmService
      .getActionPerformedPushNotification()
      .subscribe(({ notification }) => {
        this.onUserAction('', notification);
      });
  }

  getFcmToken() {
    // const platform = Capacitor.getPlatform().toLowerCase();
    // if (['ios', 'android'].includes(platform)) {
      this.fcmService.getFcmRegistrationToken().subscribe(async (token) => {
        const user = this.userService.getCurrentUser();
        if (user) {
          const fcmObj = {
            login_from: this.isNative ? 'app' : 'web',
            token: token?.value,
          };
          await this.fcmDataService.saveFcm(fcmObj).toPromise();
        }
      });
    // }
  }

  requestNotificationPermission() {
    if (!this.swPush.isEnabled) {
      // TODO: If Notifications are not enabled.
      return;
    }
    this.swPush
      .requestSubscription({
        serverPublicKey: this.publicKey,
      })
      .then((sub) => {
        // It will return the endpoint and keys which will help us with sending the notification payload from server.
      })
      .catch((err) => console.log(err));
  }

  onNotificationReceive() {
    this.swPush.messages.subscribe((message) => {
      // TODO: On notification received
    });
  }

  onNotificationsAction() {
    this.swPush.notificationClicks.subscribe(({ action, notification }) => {
      this.onUserAction(action, notification);
    });
  }

  onUserAction(action: string, notification: PushNotification | NotificationOptions) {
    if (notification) {
      const data = notification?.data;
      if (data?.eventId) {
        this.router.navigate(['/event/details/', data.eventId]);
      }
    }
  }

  ngOnInit() { }
}
